import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye, EyeSlash, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { platform } from "@/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Dedicated login page for the SaaS Admin supervision dashboard.
 *
 * This page is intentionally separate from the normal tenant login page.
 * It only accepts users with role === "saas_admin".
 */
export default function MonitoringLogin() {
  const { user } = useAuth();
  const [email, setEmail] = useState("saas_admin@acta-documentum.fr");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If the SaaS Admin is already authenticated, show the supervision dashboard.
  if (user?.role === "saas_admin") {
    return <Navigate to="/monpremiermonitoring.fr/dashboard" replace />;
  }

  // If another authenticated role opens this private entrypoint, deny access.
  if (user && user.role !== "saas_admin") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 aurora-bg">
        <div
          className="max-w-md w-full rounded-[24px] p-6"
          style={{
            background: "var(--do-card-bg)",
            border: "1px solid var(--do-border)",
            boxShadow: "var(--do-card-shadow)",
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <WarningCircle size={28} weight="bold" />
            <h1 className="font-heading text-xl font-bold" style={{ color: "var(--do-ink)" }}>
              Accès refusé
            </h1>
          </div>
          <p className="text-sm" style={{ color: "var(--do-muted)" }}>
            Cette page est réservée au compte SaaS Admin plateforme.
          </p>
        </div>
      </div>
    );
  }

  /**
   * Authenticate against the backend, then verify the returned role.
   */
  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const data = await platform.loginSaasAdmin(email.trim(), password);

      if (data?.user?.role !== "saas_admin") {
        setError("Accès refusé : ce compte n'est pas un SaaS Admin.");
        return;
      }

      // The backend sets HttpOnly cookies. Reloading lets AuthContext refresh /auth/me cleanly.
      window.location.href = "/monpremiermonitoring.fr/dashboard";
    } catch (err) {
      setError(err?.response?.data?.detail || "Connexion supervision impossible.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 aurora-bg" data-testid="monitoring-login-page">
      <div className="w-full max-w-md">
        <div
          className="rounded-[28px] p-6 sm:p-8"
          style={{
            background: "var(--do-card-bg)",
            border: "1px solid var(--do-border)",
            boxShadow: "var(--do-card-shadow)",
          }}
        >
          <div className="flex flex-col items-center text-center mb-7">
            <div
              className="h-14 w-14 rounded-[18px] flex items-center justify-center mb-4"
              style={{
                background: "linear-gradient(135deg, var(--do-blue), var(--do-navy))",
                color: "#FFFFFF",
              }}
            >
              <ShieldCheck size={28} weight="bold" />
            </div>

            <div className="label-eyebrow mb-2">Accès privé</div>
            <h1 className="font-heading text-2xl font-bold" style={{ color: "var(--do-ink)" }}>
              Supervision Acta
            </h1>
            <p className="text-sm mt-2" style={{ color: "var(--do-muted)" }}>
              Connexion réservée au compte SaaS Admin plateforme.
            </p>
          </div>

          {error && (
            <div
              className="mb-4 rounded-[14px] p-3 text-sm flex gap-2"
              style={{
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.25)",
                color: "var(--do-ink)",
              }}
              data-testid="monitoring-login-error"
            >
              <WarningCircle size={18} weight="bold" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-1">Email SaaS Admin</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full px-4 py-3 rounded-[12px] border text-sm"
                style={{
                  background: "var(--do-bg-solid)",
                  borderColor: "var(--do-border)",
                  color: "var(--do-ink)",
                }}
                autoComplete="username"
                data-testid="monitoring-email-input"
                required
              />
            </div>

            <div>
              <label className="label-eyebrow block mb-1">Mot de passe</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full px-4 py-3 pr-12 rounded-[12px] border text-sm"
                  style={{
                    background: "var(--do-bg-solid)",
                    borderColor: "var(--do-border)",
                    color: "var(--do-ink)",
                  }}
                  autoComplete="current-password"
                  data-testid="monitoring-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--do-muted)" }}
                  data-testid="monitoring-password-toggle"
                  aria-label="Afficher ou masquer le mot de passe"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 rounded-[12px] text-sm font-bold disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg, var(--do-blue), var(--do-navy))",
                color: "#FFFFFF",
              }}
              data-testid="monitoring-login-submit"
            >
              {loading ? "Connexion..." : "Entrer dans la supervision"}
            </button>
          </form>

          <div className="text-xs text-center mt-5" style={{ color: "var(--do-muted)" }}>
            Cette entrée est séparée du login client classique.
          </div>
        </div>
      </div>
    </div>
  );
}
